import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';
const UPLOAD_CONCURRENCY = 10;

const DealerEventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dealerToken } = useAuth();
  const [event, setEvent] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = { headers: { Authorization: `Bearer ${dealerToken}` } };

  const load = async () => {
    try {
      const [evRes, visRes, phRes] = await Promise.all([
        axios.get(`${API_URL}/api/dealer/events/${id}`, authHeaders),
        axios.get(`${API_URL}/api/dealer/events/${id}/visitors`, authHeaders),
        axios.get(`${API_URL}/api/dealer/events/${id}/photos`, authHeaders),
      ]);
      setEvent(evRes.data);
      setVisitors(visRes.data);
      setPhotos(phRes.data);
    } catch (err) {
      if (err.response?.status === 404) navigate('/dealer/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    e.target.value = '';
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length, duplicates: 0, errors: [] });

    let done = 0, duplicates = 0;
    const errors = [];

    for (let i = 0; i < files.length; i += UPLOAD_CONCURRENCY) {
      const batch = files.slice(i, i + UPLOAD_CONCURRENCY);
      await Promise.all(batch.map(async (file) => {
        try {
          const formData = new FormData();
          formData.append('photo', file);
          const res = await axios.post(
            `${API_URL}/api/dealer/events/${id}/upload-photos`,
            formData,
            { ...authHeaders, headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' } }
          );
          if (res.data.duplicate) duplicates++;
        } catch (err) {
          errors.push(err.response?.data?.error || file.name);
        } finally {
          done++;
          setUploadProgress({ done, total: files.length, duplicates, errors: [...errors] });
        }
      }));
    }
    setUploading(false);
    load();
  };

  const copyRegisterUrl = () => {
    const token = event?.register_token;
    const url = token
      ? `${window.location.origin}/em/register?token=${token}`
      : `${window.location.origin}/em/register?event_id=${id}`;
    navigator.clipboard.writeText(url);
    alert('Registration URL copied!');
  };

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>Loading...</div>;
  if (!event) return null;

  const latestRun = event.matching_runs?.[0];
  const sc = STATUS_COLORS[event.status] || STATUS_COLORS.active;

  return (
    <div style={s.page}>
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
        <button style={s.backBtn} onClick={() => navigate('/dealer/dashboard')}>← Dashboard</button>
        <span style={{ fontWeight: 700, color: '#1a1a2e' }}>{event.name}</span>
        <span style={{ ...s.badge, background: sc.bg, color: sc.color }}>{sc.label}</span>
      </div>

      <div style={s.container}>
        {/* Stats */}
        <div style={s.statsRow}>
          <StatCard icon="👥" label="Visitors" value={visitors.length} />
          <StatCard icon="🖼" label="Photos" value={photos.length} />
          <StatCard icon="✅" label="Matches" value={latestRun?.total_matches || 0} />
          <StatCard icon="📅" label="Event Date" value={new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} small />
        </div>

        {/* Actions */}
        <div style={s.actions}>
          <button style={s.actionBtn} onClick={copyRegisterUrl}>🔗 Copy Registration URL</button>
          <a href={`${API_URL}/api/photos/qr/${id}`} target="_blank" rel="noreferrer">
            <button style={s.actionBtn}>📲 View QR Code</button>
          </a>
          <button style={{ ...s.actionBtn, ...(uploading ? s.disabled : {}) }}
            onClick={() => !uploading && fileInputRef.current?.click()} disabled={uploading}>
            📤 Upload Photos
          </button>
          <input ref={fileInputRef} type="file" multiple accept=".jpg,.jpeg,.png"
            style={{ display: 'none' }} onChange={handleUpload} />
        </div>

        {/* Upload progress */}
        {uploadProgress && (
          <div style={s.uploadBox}>
            <div style={s.uploadHeader}>
              <span style={{ fontWeight: '700', fontSize: '0.9rem' }}>
                {uploading
                  ? `Uploading… ${uploadProgress.done} / ${uploadProgress.total}`
                  : `Done — ${uploadProgress.done - uploadProgress.duplicates - uploadProgress.errors.length} new, ${uploadProgress.duplicates} duplicate${uploadProgress.duplicates !== 1 ? 's' : ''}, ${uploadProgress.errors.length} failed`}
              </span>
              {!uploading && <button style={s.dismissBtn} onClick={() => setUploadProgress(null)}>✕</button>}
            </div>
            <div style={s.progressTrack}>
              <div style={{ ...s.progressFill, width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%`, background: uploading ? '#4f46e5' : uploadProgress.errors.length > 0 ? '#f59e0b' : '#16a34a' }} />
            </div>
            {uploadProgress.errors.length > 0 && (
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#dc2626' }}>
                Failed: {uploadProgress.errors.slice(0, 5).join(', ')}
              </p>
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={s.tabs}>
          {['overview', 'visitors', 'photos', 'runs'].map(t => (
            <button key={t} style={{ ...s.tab, ...(tab === t ? s.tabActive : {}) }} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
              {t === 'visitors' && ` (${visitors.length})`}
              {t === 'photos' && ` (${photos.length})`}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === 'overview' && (
          <div style={s.infoGrid}>
            <InfoRow label="Event Name" value={event.name} />
            <InfoRow label="Date" value={new Date(event.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} />
            <InfoRow label="Venue" value={event.venue} />
            {event.description && <InfoRow label="Description" value={event.description} />}
            <InfoRow label="Status" value={sc.label} />
            <InfoRow label="Indexing" value={event.indexed_status || 'pending'} />
            <InfoRow label="Published" value={event.published_at ? new Date(event.published_at).toLocaleString('en-IN') : 'Not published yet'} />
            <InfoRow label="Created" value={new Date(event.created_at).toLocaleString('en-IN')} />
          </div>
        )}

        {/* Visitors */}
        {tab === 'visitors' && (
          visitors.length === 0 ? <Empty msg="No visitors registered yet." /> : (
            <table style={s.table}>
              <thead><tr style={s.thead}>{['Name','Mobile','Registered','Matches','Gallery'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {visitors.map(v => (
                  <tr key={v.id} style={s.tr}>
                    <td style={s.td}>{v.full_name}</td>
                    <td style={s.td}>{v.mobile_number}</td>
                    <td style={s.td}>{new Date(v.registered_at).toLocaleString('en-IN')}</td>
                    <td style={s.td}><span style={{ fontWeight: '700', color: v.match_count > 0 ? '#166534' : '#888' }}>{v.match_count}</span></td>
                    <td style={s.td}>
                      {v.gallery_url
                        ? <button style={s.copyBtn} onClick={() => navigator.clipboard.writeText(v.gallery_url)}>📋 Copy</button>
                        : <span style={{ color: '#bbb', fontSize: '0.8rem' }}>Not published</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* Photos */}
        {tab === 'photos' && (
          photos.length === 0 ? <Empty msg="No photos yet. Upload using the button above." /> : (
            <table style={s.table}>
              <thead><tr style={s.thead}>{['File Name','Uploaded','Processed'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {photos.slice(0, 100).map(p => (
                  <tr key={p.id} style={s.tr}>
                    <td style={s.td}>{p.file_name}</td>
                    <td style={s.td}>{new Date(p.uploaded_at).toLocaleString('en-IN')}</td>
                    <td style={s.td}>{p.is_processed ? '✅' : '⏳'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* Runs */}
        {tab === 'runs' && (
          !event.matching_runs?.length ? <Empty msg="No matching runs yet." /> : (
            <table style={s.table}>
              <thead><tr style={s.thead}>{['Run','Threshold','Status','Photos','Matches','Started'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {event.matching_runs.map(r => (
                  <tr key={r.id} style={s.tr}>
                    <td style={s.td}>#{r.id}</td>
                    <td style={s.td}>{Math.round(r.threshold * 100)}%</td>
                    <td style={s.td}><span style={{ fontWeight: '600', color: r.status === 'completed' ? '#166534' : r.status === 'failed' ? '#dc2626' : '#854d0e' }}>{r.status}</span></td>
                    <td style={s.td}>{r.processed_photos}/{r.total_photos}</td>
                    <td style={s.td}>{r.total_matches}</td>
                    <td style={s.td}>{new Date(r.started_at).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
    </div>
  );
};

const STATUS_COLORS = {
  active:    { bg: '#f0fdf4', color: '#166534', label: 'Active' },
  matching:  { bg: '#fef9c3', color: '#854d0e', label: 'Matching…' },
  completed: { bg: '#eff6ff', color: '#1e40af', label: 'Matched' },
  published: { bg: '#f5f3ff', color: '#5b21b6', label: 'Published' },
};

const StatCard = ({ icon, label, value, small }) => (
  <div style={s.statCard}>
    <div style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>{icon}</div>
    <div style={{ fontSize: small ? '1rem' : '2rem', fontWeight: '800', color: '#1a1a2e', lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={s.infoRow}>
    <span style={s.infoLabel}>{label}</span>
    <span style={{ color: '#1a1a2e', fontSize: '0.95rem' }}>{value}</span>
  </div>
);

const Empty = ({ msg }) => <div style={{ textAlign: 'center', padding: '3rem', color: '#888', background: '#fff', borderRadius: '1.25rem', border: '1px solid #f0f0f0' }}>{msg}</div>;

const s = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', system-ui, sans-serif" },
  nav: { background: '#0f3460', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' },
  backBtn: { background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', cursor: 'pointer', fontSize: '0.875rem' },
  navTitle: { color: '#fff', fontWeight: '700', flex: 1, fontSize: '1.05rem' },
  badge: { borderRadius: '50px', padding: '0.3rem 0.875rem', fontSize: '0.8rem', fontWeight: '700' },
  container: { maxWidth: '1100px', margin: '0 auto', padding: '2rem 1.5rem' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  statCard: { background: '#fff', borderRadius: '1.25rem', padding: '1.25rem', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' },
  actions: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' },
  actionBtn: { padding: '0.6rem 1rem', background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '0.75rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', color: '#444' },
  disabled: { opacity: 0.5, cursor: 'not-allowed' },
  uploadBox: { background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '1rem', padding: '1rem 1.25rem', marginBottom: '1rem' },
  uploadHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem', color: '#1a1a2e' },
  dismissBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '1rem' },
  progressTrack: { height: '8px', background: '#f0f0f0', borderRadius: '99px', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: '99px', transition: 'width 0.2s ease' },
  tabs: { display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb' },
  tab: { padding: '0.6rem 1rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', color: '#888', fontSize: '0.9rem', borderBottom: '2px solid transparent', marginBottom: '-2px' },
  tabActive: { color: '#0f3460', borderBottomColor: '#0f3460' },
  infoGrid: { background: '#fff', borderRadius: '1.25rem', overflow: 'hidden', border: '1px solid #f0f0f0' },
  infoRow: { display: 'flex', padding: '0.875rem 1.25rem', borderBottom: '1px solid #f5f5f5', alignItems: 'flex-start', gap: '1rem' },
  infoLabel: { width: '150px', minWidth: '150px', fontSize: '0.8rem', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', paddingTop: '0.1rem' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '1.25rem', overflow: 'hidden', border: '1px solid #f0f0f0' },
  thead: { background: '#f8fafc' },
  th: { padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' },
  tr: { borderBottom: '1px solid #f5f5f5' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#444' },
  copyBtn: { padding: '0.3rem 0.625rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', color: '#1e40af' },
};

export default DealerEventDetail;
